import { type PowerLabData } from "../../../../power-bubble-chart/powerBubbleChartData"

export type ComputeFragment = {
  id: string
  lab: PowerLabData
  value: number
}

export type ComputeFragmentRect = {
  fragment: ComputeFragment
  x: number
  y: number
  width: number
  height: number
}

type LayoutRequest = {
  fragments: ComputeFragment[]
  x: number
  y: number
  width: number
  height: number
}

type FragmentSplit = {
  firstFragments: ComputeFragment[]
  secondFragments: ComputeFragment[]
  firstShare: number
}

const getFragmentValueSum = (valueFragments: ComputeFragment[]): number => {
  let valueSum = 0
  for (const fragment of valueFragments) valueSum += fragment.value
  return valueSum
}

const getSplitCandidateDelta = (
  fragments: ComputeFragment[],
  splitIndex: number,
  totalValue: number
): number => (
  Math.abs(totalValue / 2 - getFragmentValueSum(fragments.slice(0, splitIndex)))
)

// Finds the cut that keeps the recursive treemap halves visually balanced.
const getBalancedSplitIndex = (
  fragments: ComputeFragment[],
  totalValue: number
): number => {
  const possibleSplitIndexes = fragments.slice(1).map((_fragment, index) => index + 1)
  const sortedSplitIndexes = possibleSplitIndexes.sort((first, second) => {
    const dFirst = getSplitCandidateDelta(fragments, first, totalValue)
    const dSecond = getSplitCandidateDelta(fragments, second, totalValue)
    if (Math.abs(dFirst - dSecond) > Math.max(1e-9 * totalValue, 1e-12)) {
      return dFirst - dSecond
    }
    const boundaryFirst = fragments[first - 1].lab.regionId !== fragments[first].lab.regionId
    const boundarySecond = fragments[second - 1].lab.regionId !== fragments[second].lab.regionId
    if (boundaryFirst !== boundarySecond) return boundaryFirst ? -1 : 1
    return first - second
  })
  return sortedSplitIndexes[0] ?? 1
}

// No rendered cell should be thinner than this on either side — below it a lab
// becomes a "pancake" sliver that breaks the layout. The recursion enforces it
// (see getLayoutForRequest): it only ever creates rects that are at least this
// big in BOTH dimensions, and clamps each split so neither piece drops under
// it. A rect too small to hold two such cells renders only its largest lab
// (the smaller ones are folded away rather than sliced into slivers). Sized so
// the 2px per-side whitespace still fits and leaves a small but non-broken box.
export const MIN_CELL_SIDE_PX = 8

// Transition mode: while true, the layout allows arbitrarily small cells (no
// min-size clamp, no anti-pancake fold). The transition engine turns this on so
// a cell can smoothly grow from ~0 or shrink to ~0 during a morph; the static
// (settled) layout keeps it off so no permanent slivers appear. Module flag
// (not a threaded param) because layout is synchronous — the engine sets it,
// calls layout, resets it, within one call.
let LAYOUT_ALLOW_TINY = false
export const setLayoutAllowTiny = (value: boolean): void => { LAYOUT_ALLOW_TINY = value }
export const getLayoutAllowTiny = (): boolean => LAYOUT_ALLOW_TINY

// A split along an axis of `length` (guaranteed ≥ 2·MIN by the caller) that
// keeps BOTH resulting pieces at least MIN_CELL_SIDE_PX, even for a tiny share.
// In allow-tiny (transition) mode the pieces may be any size.
const clampSplitLength = (length: number, share: number): number =>
  LAYOUT_ALLOW_TINY
    ? Math.max(0, Math.min(length, length * share))
    : Math.min(length - MIN_CELL_SIDE_PX, Math.max(MIN_CELL_SIDE_PX, length * share))

const getLargestFragment = (fragments: ComputeFragment[]): ComputeFragment =>
  fragments.reduce((largest, fragment) =>
    fragment.value > largest.value ? fragment : largest)

const getHorizontalSplitRects = (
  request: LayoutRequest,
  split: FragmentSplit
): ComputeFragmentRect[] => {
  const firstWidth = clampSplitLength(request.width, split.firstShare)
  return [
    ...layoutComputeFragments(split.firstFragments, request.x, request.y, firstWidth, request.height),
    ...layoutComputeFragments(
      split.secondFragments,
      request.x + firstWidth,
      request.y,
      request.width - firstWidth,
      request.height
    ),
  ]
}

const getVerticalSplitRects = (
  request: LayoutRequest,
  split: FragmentSplit
): ComputeFragmentRect[] => {
  const firstHeight = clampSplitLength(request.height, split.firstShare)
  return [
    ...layoutComputeFragments(split.firstFragments, request.x, request.y, request.width, firstHeight),
    ...layoutComputeFragments(
      split.secondFragments,
      request.x,
      request.y + firstHeight,
      request.width,
      request.height - firstHeight
    ),
  ]
}

// STICKY split structure per node. Slice-and-dice is squarified but UNSTABLE:
// as the cell set churns year to year (labs crossing the frontier in/out of the
// "Rest of X" aggregate in Plan A, tails splitting, …) the value-balanced cut
// and the aspect-driven axis both change, so cells jump across cuts and flip
// rows↔columns between steps — a "huge layout shift in one frame". So the cut is
// decided ONCE (which cells go to the first child, as a set) and then REUSED for
// any nearby cell set: when this node is laid out again with a few cells added
// or removed, the shared cells keep the side they were on and only newcomers are
// spliced in (next to their stable-order neighbour). Consecutive layouts thus
// share structure — cells only grow/shrink and newcomers appear locally, never a
// wholesale reshuffle. Proportions always track current values.
type SplitStructure = { horizontal: boolean; firstIds: Set<string> }
const splitStructureCache = new Map<string, { ids: string[]; structure: SplitStructure }>()

// Reuse the most-overlapping previously-seen structure for this node (same cells
// give or take a few), else compute a fresh value-balanced cut.
const deriveSplitStructure = (
  fragments: ComputeFragment[],
  totalValue: number,
  aspectHorizontal: boolean,
): SplitStructure => {
  const ids = fragments.map(f => f.id)
  const idSet = new Set(ids)
  let best: { ids: string[]; structure: SplitStructure } | null = null
  let bestShared = 0
  for (const cached of splitStructureCache.values()) {
    let shared = 0
    for (const id of cached.ids) if (idSet.has(id)) shared++
    // Only reuse a structure that is "the same node give or take a few cells":
    // most of both sets must be shared. Guards against matching a different node
    // whose ids coincidentally overlap.
    const enough = shared >= 1 && shared * 2 >= Math.max(ids.length, cached.ids.length)
    if (enough && shared > bestShared) { bestShared = shared; best = cached }
  }
  if (best) {
    const bestIds = new Set(best.ids)
    // Shared cells keep the side they were on; a newcomer joins its ordered
    // predecessor's side (the first cell defaults to the first child).
    const firstIds = new Set<string>()
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]
      if (best.structure.firstIds.has(id)) { firstIds.add(id); continue }  // was first
      if (bestIds.has(id)) continue                                        // was second
      const prev = i > 0 ? ids[i - 1] : null                               // newcomer
      if (prev === null || firstIds.has(prev)) firstIds.add(id)
    }
    // Never leave a side empty (would collapse the split).
    if (firstIds.size > 0 && firstIds.size < ids.length) return { horizontal: best.structure.horizontal, firstIds }
  }
  const index = getBalancedSplitIndex(fragments, totalValue)
  return { horizontal: aspectHorizontal, firstIds: new Set(ids.slice(0, index)) }
}

const getSplitRects = (
  request: LayoutRequest,
  totalValue: number
): ComputeFragmentRect[] => {
  const ids = request.fragments.map(fragment => fragment.id)
  const key = ids.join("|")
  let entry = splitStructureCache.get(key)
  if (!entry) {
    entry = { ids, structure: deriveSplitStructure(request.fragments, totalValue, request.width >= request.height) }
    splitStructureCache.set(key, entry)
  }
  const firstFragments: ComputeFragment[] = []
  const secondFragments: ComputeFragment[] = []
  for (const fragment of request.fragments) {
    (entry.structure.firstIds.has(fragment.id) ? firstFragments : secondFragments).push(fragment)
  }
  const split: FragmentSplit = {
    firstFragments,
    secondFragments,
    firstShare: getFragmentValueSum(firstFragments) / totalValue,
  }
  if (firstFragments.length === 0 || secondFragments.length === 0) {
    // Degenerate (shouldn't happen) — fall back to a value-balanced cut.
    const index = getBalancedSplitIndex(request.fragments, totalValue)
    split.firstFragments = request.fragments.slice(0, index)
    split.secondFragments = request.fragments.slice(index)
    split.firstShare = getFragmentValueSum(split.firstFragments) / totalValue
  }
  return entry.structure.horizontal ? getHorizontalSplitRects(request, split) : getVerticalSplitRects(request, split)
}

const getLayoutForRequest = (request: LayoutRequest): ComputeFragmentRect[] => {
  if (request.fragments.length === 0 || request.width <= 0 || request.height <= 0) return []
  if (request.fragments.length === 1) return [{ fragment: request.fragments[0], ...request }]
  const totalValue = getFragmentValueSum(request.fragments)
  if (totalValue <= 0) return []
  // Anti-pancake stop condition. Split only when the result can be two cells
  // that are each ≥ MIN on both sides: the rect must not already be thin
  // (shorter side ≥ MIN) and its longer side must hold two min-width cells
  // (≥ 2·MIN). The split (along the longer axis, clamped) then hands each
  // child a ≥MIN length on that axis while it inherits the parent's ≥MIN short
  // side — so every rect the recursion ever produces is ≥MIN in BOTH
  // dimensions. When neither holds, there's no room to subdivide without
  // slivering, so just render the largest lab here.
  const shortSide = Math.min(request.width, request.height)
  const longSide = Math.max(request.width, request.height)
  if (!LAYOUT_ALLOW_TINY && (shortSide < MIN_CELL_SIDE_PX || longSide < 2 * MIN_CELL_SIDE_PX)) {
    return [{ fragment: getLargestFragment(request.fragments), ...request }]
  }
  return getSplitRects(request, totalValue)
}

export const layoutComputeFragments = (
  fragments: ComputeFragment[],
  x: number,
  y: number,
  width: number,
  height: number
): ComputeFragmentRect[] => (
  getLayoutForRequest({ fragments, x, y, width, height })
)

// Default pixel gap inserted between regional groups in the linear layout, so
// the three country groups read as visually grouped. Callers may override this
// per-instance via the linear layout's `countryGroupGapPx` argument.
export const LINEAR_REGION_GAP_PX = 6

// Lays fragments in a single horizontal row, sized proportionally to compute.
// When `groupByCountry` is true (the default), fragments stay in the natural
// US → CH → RoW order from `getComputeFragments` (sorted by value within each
// region) and a pixel gap of `countryGroupGapPx` separates the three country
// groups. When false, all fragments are sorted by value descending across the
// whole world.
export const layoutComputeFragmentsLinear = (
  fragments: ComputeFragment[],
  x: number,
  y: number,
  width: number,
  height: number,
  groupByCountry: boolean = true,
  countryGroupGapPx: number = LINEAR_REGION_GAP_PX
): ComputeFragmentRect[] => {
  if (fragments.length === 0 || width <= 0 || height <= 0) return []
  const totalValue = getFragmentValueSum(fragments)
  if (totalValue <= 0) return []
  const ordered = groupByCountry
    ? fragments
    : [...fragments].sort((a, b) => b.value - a.value)
  let regionBoundaries = 0
  if (groupByCountry) {
    for (let i = 1; i < ordered.length; i++) {
      if (ordered[i - 1].lab.regionId !== ordered[i].lab.regionId) {
        regionBoundaries++
      }
    }
  }
  const totalGap = Math.min(width * 0.5, regionBoundaries * countryGroupGapPx)
  const usableWidth = Math.max(0, width - totalGap)
  const out: ComputeFragmentRect[] = []
  let cursorX = x
  for (let i = 0; i < ordered.length; i++) {
    const fragment = ordered[i]
    const fragmentWidth = (fragment.value / totalValue) * usableWidth
    out.push({
      fragment,
      x: cursorX,
      y,
      width: fragmentWidth,
      height,
    })
    cursorX += fragmentWidth
    const isLast = i === ordered.length - 1
    if (
      groupByCountry &&
      !isLast &&
      fragment.lab.regionId !== ordered[i + 1].lab.regionId
    ) {
      cursorX += totalGap / Math.max(1, regionBoundaries)
    }
  }
  return out
}
